/**
 * Inline editors for the Quote Library, embedded RIGHT INSIDE the Calculator.
 *
 * Why here and not buried in Settings:
 *   The custom Quote Library (own trades, own add-on prices) is the killer
 *   feature of QuoteSnapp. Putting it behind a Settings page hides it from
 *   90% of users. Surfacing "+ Add your own trade" and "+ Add your own add-on"
 *   right where the user is BUILDING a quote means they discover it the
 *   moment they need it.
 *
 * Auth handling: when the user is not signed in, these inline editors
 * gracefully degrade to a "Sign up free to save your own trades" CTA so
 * the marketing-page calculator doesn't show broken forms.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Plus, Pencil, Trash2, Lock,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEASUREMENT_OPTIONS = [
  { value: 'square_feet', label: 'Square feet' },
  { value: 'linear_feet', label: 'Linear feet' },
  { value: 'hourly',      label: 'Hourly' },
  { value: 'count',       label: 'Per unit' },
];

// ─── Project Type inline manager ───────────────────────────────────────────

export const InlineAddProjectType = ({ user, refreshLibrary, setProjectTypes }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [measurement, setMeasurement] = useState('square_feet');
  const [rate, setRate] = useState('');
  const [hours, setHours] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    setErr('');

    const trimmed = label.trim();
    const matRate = parseFloat(rate) || 0;
    const lbrHours = parseFloat(hours) || 0.2;

    // Logged-in users: persist to library. Logged-out: just add locally so
    // they can use it in this quote (and we'll show a "sign up to save" hint).
    if (user) {
      try {
        await axios.post(`${API}/quote-library/project-types`, {
          label: trimmed,
          material_rate: matRate,
          labor_hours: lbrHours,
          measurement_type: measurement,
        }, { withCredentials: true });
        setLabel(''); setRate(''); setHours(''); setMeasurement('square_feet');
        setOpen(false);
        await refreshLibrary?.();
      } catch (ex) {
        setErr(ex.response?.data?.detail || 'Could not save trade. Try again.');
      } finally { setBusy(false); }
      return;
    }

    // Guest: add to local state only.
    const slug = `custom-${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${Date.now().toString(36)}`;
    const newPT = {
      id: slug,
      slug,
      value: slug,
      label: trimmed,
      material_rate: matRate,
      materialRate: matRate,
      labor_hours: lbrHours,
      laborHours: lbrHours,
      measurement_type: measurement,
      measurementType: measurement,
      is_default: false,
      _local: true,
    };
    setProjectTypes?.((prev) => [...prev, newPT]);
    setLabel(''); setRate(''); setHours(''); setMeasurement('square_feet');
    setOpen(false);
    setBusy(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        data-testid="calc-add-trade-toggle"
      >
        <Plus className="w-3.5 h-3.5" /> Add your own trade
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-2 p-3 rounded-lg border-2 border-blue-200 bg-blue-50/50 space-y-2"
      data-testid="calc-add-trade-form"
    >
      <Input
        autoFocus
        placeholder="Trade name (e.g. Tile installation)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        data-testid="calc-add-trade-label"
      />
      <div className="grid grid-cols-3 gap-2">
        <select
          className="rounded-md border border-slate-300 px-2 py-2 text-sm bg-white"
          value={measurement}
          onChange={(e) => setMeasurement(e.target.value)}
          data-testid="calc-add-trade-measurement"
        >
          {MEASUREMENT_OPTIONS.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <Input
          type="number" step="0.01" placeholder="Mat. rate"
          value={rate} onChange={(e) => setRate(e.target.value)}
          data-testid="calc-add-trade-rate"
        />
        <Input
          type="number" step="0.01" placeholder="Hrs/unit"
          value={hours} onChange={(e) => setHours(e.target.value)}
          data-testid="calc-add-trade-hours"
        />
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      {!user && (
        <p className="text-xs text-slate-500" data-testid="calc-add-trade-guest-hint">
          <Lock className="inline w-3 h-3 mr-1 align-text-bottom" />
          Adds to this quote only.{' '}
          <Link to="/signup" className="text-blue-700 font-semibold underline">Sign up free</Link>{' '}
          to save it to your library.
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="submit" disabled={busy || !label.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
          data-testid="calc-add-trade-save"
        >
          {busy ? 'Saving…' : user ? 'Save trade' : 'Add to this quote'}
        </Button>
        <button
          type="button"
          onClick={() => { setOpen(false); setErr(''); }}
          className="text-xs text-slate-500 hover:text-slate-700"
          data-testid="calc-add-trade-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Add-On row with always-editable price ─────────────────────────────────

const AddOnRow = ({
  addon, selected, onToggle, user, refreshLibrary, setAddOns,
}) => {
  // Local price + label state lets the user type freely; we sync to the
  // global addOns array on every keystroke and PATCH the library on blur if
  // they're authenticated. Logged-out visitors get per-quote overrides only.
  const [price, setPrice] = useState(String(addon.price ?? 0));
  const [label, setLabel] = useState(addon.label);
  const [editingLabel, setEditingLabel] = useState(false);
  const [busy, setBusy] = useState(false);

  // Keep local state in sync when the addOns array changes from elsewhere
  // (e.g. refresh after the user adds a new add-on above).
  React.useEffect(() => {
    setPrice(String(addon.price ?? 0));
    setLabel(addon.label);
  }, [addon.price, addon.label]);

  const onPriceChange = (e) => {
    const next = e.target.value;
    setPrice(next);
    // Update the in-memory array immediately so the live Quote Preview
    // recalculates using the new price right as the user types.
    const numeric = parseFloat(next);
    if (Number.isFinite(numeric)) {
      setAddOns?.((prev) =>
        prev.map((a) => (a.id === addon.id ? { ...a, price: numeric } : a))
      );
    }
  };

  const persistPriceOnBlur = async () => {
    if (!user) return; // logged-out: per-quote override only, no save
    const numeric = parseFloat(price);
    if (!Number.isFinite(numeric) || numeric === addon.price) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/quote-library/add-ons/${addon.id}`,
        { price: numeric },
        { withCredentials: true }
      );
    } catch { /* silent — keep the live price for this quote */ }
    finally { setBusy(false); }
  };

  const onLabelChange = (e) => {
    const next = e.target.value;
    setLabel(next);
    if (next.trim()) {
      setAddOns?.((prev) =>
        prev.map((a) => (a.id === addon.id ? { ...a, label: next } : a))
      );
    }
  };

  const finishLabelEdit = async () => {
    setEditingLabel(false);
    const trimmed = label.trim();
    if (!trimmed) {
      // Revert empty
      setLabel(addon.label);
      return;
    }
    if (!user || trimmed === addon.label) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/quote-library/add-ons/${addon.id}`,
        { label: trimmed },
        { withCredentials: true }
      );
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${addon.label}" from your add-ons?`)) return;
    if (!user) {
      // Logged-out: just hide from local state.
      setAddOns?.((prev) => prev.filter((a) => a.id !== addon.id));
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/quote-library/add-ons/${addon.id}`, { withCredentials: true });
      await refreshLibrary?.();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors group"
      data-testid={`addon-row-${addon.id}`}
    >
      <input
        type="checkbox" id={`ao-${addon.id}`}
        checked={selected} onChange={onToggle}
        className="w-4 h-4 accent-blue-600 shrink-0"
      />
      {editingLabel ? (
        <input
          type="text"
          autoFocus
          value={label}
          onChange={onLabelChange}
          onBlur={finishLabelEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setLabel(addon.label); setEditingLabel(false); }
          }}
          disabled={busy}
          className="flex-1 h-8 text-sm rounded-md border border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 px-2 bg-white"
          aria-label={`Rename ${addon.label}`}
          data-testid={`addon-label-input-${addon.id}`}
        />
      ) : (
        <label htmlFor={`ao-${addon.id}`} className="flex-1 cursor-pointer text-sm">
          {addon.label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setEditingLabel(true)}
        className="p-1.5 text-slate-300 hover:text-blue-700 hover:bg-blue-50 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0"
        aria-label={`Edit ${addon.label} name`}
        data-testid={`addon-edit-label-${addon.id}`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-slate-500 text-sm">$</span>
        <input
          type="number"
          step="0.01"
          inputMode="decimal"
          value={price}
          onChange={onPriceChange}
          onBlur={persistPriceOnBlur}
          onClick={(e) => e.stopPropagation()}
          disabled={busy}
          className="w-20 h-8 text-sm font-semibold text-slate-800 text-right rounded-md border border-slate-200 hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 px-2 bg-white"
          aria-label={`Price for ${addon.label}`}
          data-testid={`addon-price-input-${addon.id}`}
        />
      </div>
      <button
        type="button"
        onClick={remove}
        disabled={busy}
        className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0"
        aria-label={`Remove ${addon.label}`}
        data-testid={`addon-delete-${addon.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export const InlineAddOnsList = ({
  addOns, setAddOns, selectedAddOns, toggleAddOn, user, refreshLibrary,
}) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    setErr('');

    const trimmed = label.trim();
    const numericPrice = parseFloat(price) || 0;

    // Logged-in: persist to library. Logged-out: add locally only.
    if (user) {
      try {
        await axios.post(`${API}/quote-library/add-ons`, {
          label: trimmed,
          price: numericPrice,
        }, { withCredentials: true });
        setLabel(''); setPrice('');
        setOpen(false);
        await refreshLibrary?.();
      } catch (ex) {
        setErr(ex.response?.data?.detail || 'Could not save add-on. Try again.');
      } finally { setBusy(false); }
      return;
    }

    // Guest path: local-only add-on.
    const slug = `custom-${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${Date.now().toString(36)}`;
    setAddOns?.((prev) => [...prev, {
      id: slug, slug, label: trimmed, price: numericPrice, is_default: false, _local: true,
    }]);
    setLabel(''); setPrice('');
    setOpen(false);
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Add-Ons (Optional)</Label>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-800"
            data-testid="calc-add-addon-toggle"
          >
            <Plus className="w-3.5 h-3.5" /> Add custom add-on
          </button>
        )}
      </div>

      {open && (
        <form
          onSubmit={submit}
          className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50/50 space-y-2"
          data-testid="calc-add-addon-form"
        >
          <div className="grid grid-cols-[1fr,90px] gap-2">
            <Input
              autoFocus
              placeholder="Add-on name (e.g. Power washing)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="calc-add-addon-label"
            />
            <Input
              type="number" step="0.01" placeholder="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid="calc-add-addon-price"
            />
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
          {!user && (
            <p className="text-xs text-slate-500" data-testid="calc-add-addon-guest-hint">
              <Lock className="inline w-3 h-3 mr-1 align-text-bottom" />
              Adds to this quote only.{' '}
              <Link to="/signup" className="text-blue-700 font-semibold underline">Sign up free</Link>{' '}
              to save it for next time.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="submit" disabled={busy || !label.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
              data-testid="calc-add-addon-save"
            >
              {busy ? 'Saving…' : user ? 'Save add-on' : 'Add to this quote'}
            </Button>
            <button
              type="button"
              onClick={() => { setOpen(false); setErr(''); }}
              className="text-xs text-slate-500 hover:text-slate-700"
              data-testid="calc-add-addon-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {addOns.map((addon) => (
          <AddOnRow
            key={addon.id}
            addon={addon}
            selected={selectedAddOns.includes(addon.id)}
            onToggle={() => toggleAddOn(addon.id)}
            user={user}
            refreshLibrary={refreshLibrary}
            setAddOns={setAddOns}
          />
        ))}
      </div>
    </div>
  );
};
